import React from 'react';
import { Star, Award, Users, Home, ThumbsUp } from 'lucide-react';
import { Reveal, EstimateBanner } from '../components/shared';
import { PageHeader } from './Flooring';
import { business } from '../mock/mock';

const stats = [
  { icon: Home, value: '5,000+', label: 'Floors installed' },
  { icon: Star, value: '4.9/5', label: 'Average rating' },
  { icon: Users, value: '15+', label: 'Years in business' },
  { icon: ThumbsUp, value: '100%', label: 'Satisfaction focus' }
];

const values = [
  { title: 'Honest pricing', text: 'All-in quotes with no hidden fees or surprise charges. What we quote is what you pay.' },
  { title: 'One point of contact', text: 'The same expert guides your selection and oversees your installation from start to finish.' },
  { title: 'Quality that lasts', text: 'We only carry brands we’d put in our own homes, installed to last for decades.' }
];

export default function About() {
  return (
    <>
      <PageHeader
        eyebrow="About Montifloors"
        title="Here to floor you — the right way"
        sub="A locally owned, mobile flooring company built on honest advice, fair prices, and quality you can stand on."
        image="https://images.unsplash.com/photo-1575204015311-0fe377370780?auto=format&fit=crop&w=1920&q=80"
      />

      <section className="max-w-7xl mx-auto px-6 py-20 md:py-24 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <img src="https://images.unsplash.com/photo-1585128792020-803d29415281?auto=format&fit=crop&w=1200&q=80" alt="Beautifully installed wood flooring" className="rounded-2xl w-full h-[480px] object-cover shadow-lg" />
        </Reveal>
        <Reveal delay={120}>
          <span className="text-brand-clay text-xs font-semibold uppercase tracking-[0.25em]">Our story</span>
          <h2 className="font-display text-3xl md:text-[40px] font-semibold text-brand-charcoal mt-3 leading-tight text-balance">A local company with a simpler idea</h2>
          <p className="mt-5 text-brand-stone text-[17px] leading-relaxed">Montifloors began with a frustration shared by every homeowner: choosing floors under store lights, only to get them home and wonder if the color was right. Since {business.since}, we’ve solved that by bringing the showroom to you.</p>
          <p className="mt-4 text-brand-stone text-[17px] leading-relaxed">We’re proud to serve {business.serviceArea} with the selection of a major retailer and the care of a neighbor. No storefront overhead means better prices — and a whole lot less hassle for you.</p>
          <p className="mt-4 text-brand-stone text-[17px] leading-relaxed">Every project, big or small, gets our full attention and care. If you’re not thrilled, we’re not finished.</p>
        </Reveal>
      </section>

      {/* stats */}
      <section className="bg-brand-charcoal">
        <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, idx) => (
            <Reveal key={s.label} delay={idx * 80}>
              <div className="text-center">
                <s.icon size={28} className="mx-auto text-brand-clay" />
                <p className="font-display text-4xl md:text-5xl font-semibold text-brand-ivory mt-4">{s.value}</p>
                <p className="text-brand-sand/70 text-sm mt-1">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* values */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-24">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-brand-clay text-xs font-semibold uppercase tracking-[0.25em]">What we stand for</span>
            <h2 className="font-display text-3xl md:text-[42px] font-semibold text-brand-charcoal mt-3 leading-tight">Values that guide every floor</h2>
          </div>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {values.map((v, idx) => (
            <Reveal key={v.title} delay={idx * 90}>
              <div className="bg-white rounded-2xl p-8 border border-brand-line shadow-sm h-full">
                <span className="w-12 h-12 rounded-xl bg-brand-clay/10 text-brand-clay flex items-center justify-center"><Award size={22} /></span>
                <h3 className="mt-5 font-display text-xl font-semibold text-brand-charcoal">{v.title}</h3>
                <p className="mt-2 text-brand-stone text-[15px] leading-relaxed">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <EstimateBanner />
    </>
  );
}
